/**
 * Chat - Chat command processing and context menu player actions
 *
 * Routes a typed line: a slash command with a handler of its own, a slash
 * word that is a team emote, a flag bubble, an emote or a command for the
 * server, a QBot team command in CTF, or plain chat. Long text goes out in
 * chunks the wire can carry, spaced for the server's chat limit. Also the
 * context menu's social actions (whisper, ignore, votemute).
 */

import { GAME_TYPES } from '@airmash/protocol';
import { resolveFlag, toServerFlag } from '../services/Flags';
import { escapePlayerName, unescapePlayerName } from '../services/PlayerNames';
import { uiState } from '../ui/UIState';
import { sendPing } from './ClientPing';
import {
  sendChat as networkSendChat,
  sendCommand as networkSendCommand,
  sendSay as networkSendSay,
  sendTeam as networkSendTeam,
  sendWhisper as networkSendWhisper,
} from './Network';
import type Player from './Player';
import { get as playersGet, getByName as playersGetByName, requestMyFlag as playersRequestMyFlag } from './Players';
import { GAME_STATE_CODES, session } from './Session';
import {
  addChatMessage,
  chatIgnore,
  chatUnignore,
  chatVotemute,
  isEmote,
  isPlayerIgnored,
  noteFlagGate,
  openMainMenu,
  shortcutChat,
  toggleEmotes as uiToggleEmotes,
  toggleHelp,
  watchSayEcho as uiWatchSayEcho,
} from './UI';

/** The most characters in one chat, team or whisper chunk, so lines stay readable. */
const CHUNK_CHARS = 100;

/** The most characters the chat boxes take, five chunks: the chunks go a second apart, so this bounds an
 *  accidental paste to five seconds of sending rather than a minute. */
export const CHAT_INPUT_MAX_CHARS = 5 * CHUNK_CHARS;

/**
 * The most UTF-8 bytes in any chunk. The protocol writes a text's length as
 * one byte and the marshal wraps a longer one, so the server would read a
 * garbled prefix of anything bigger. A hundred characters of Chinese or of
 * emoji are three or four hundred bytes.
 */
const CHUNK_BYTES = 255;

/** The gap between chunks: the server takes two chat packets a second and leaks one back per second. */
const CHUNK_GAP_MS = 1100;

/** The most characters in one /s chunk, so a bubble stays readable. */
const SAY_CHUNK_CHARS = 30;

/** The gap between /s chunks: a bubble lives four seconds on the server, so each is read before the next. */
const SAY_CHUNK_GAP_MS = 3000;

/** /upgrade names → the server's upgrade numbers. */
const UPGRADE_NAMES: ReadonlyMap<string, string> = new Map([
  ['speed', '1'],
  ['defense', '2'],
  ['defence', '2'],
  ['def', '2'],
  ['energy', '3'],
  ['missile', '4'],
  ['missiles', '4'],
]);

/** /respawn names → the server's plane numbers. */
const PLANE_NAMES: ReadonlyMap<string, string> = new Map([
  ['predator', '1'],
  ['pred', '1'],
  ['goliath', '2'],
  ['goli', '2'],
  ['mohawk', '3'],
  ['heli', '3'],
  ['tornado', '4'],
  ['torn', '4'],
  ['prowler', '5'],
  ['prow', '5'],
]);

/** QBot command aliases → the command the bots read; the chat's hints list the same aliases. */
const QBOT_ALIASES: ReadonlyMap<string, string> = new Map([
  ['#c', '#cap'],
  ['#capture', '#cap'],
  ['#e', '#escort'],
  ['#esc', '#escort'],
  ['#r', '#recap'],
  ['#rec', '#recap'],
  ['#recover', '#recap'],
  ['#a', '#assist'],
  ['#d', '#drop'],
  ['#l', '#leader'],
  ['#s', '#status'],
  ['#dd', '#dropnow'],
  ['#dn', '#dropnow'],
]);

/** The first word of a line, up to its first space. */
function firstWord(text: string): string {
  const space = text.indexOf(' ');
  return space === -1 ? text : text.slice(0, space);
}

/** The UTF-8 size of a code point. */
function utf8Size(codePoint: number): number {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
}

/** Whether a character is whitespace, by the same rule as String.trim. */
function isSpace(char: string): boolean {
  return char.trim() === '';
}

/**
 * Splits text into chunks of at most `maxChars` characters and CHUNK_BYTES
 * bytes, each ending at the last word boundary that fits, or inside a word
 * that fits nowhere, with the whitespace between chunks dropped. Characters
 * are counted as code points, so an emoji counts once and is never cut in two.
 */
function chunkText(text: string, maxChars: number): string[] {
  const chars = Array.from(text);
  const chunks: string[] = [];
  let start = 0;
  while (start < chars.length) {
    // Each chunk starts at a character
    while (start < chars.length && isSpace(chars[start]!)) {
      start++;
    }
    if (start === chars.length) {
      break;
    }
    // The longest run from start that fits, and the last boundary inside it
    let end = start;
    let bytes = 0;
    let boundary = -1;
    for (; end < chars.length; end++) {
      const char = chars[end]!;
      if (isSpace(char)) {
        boundary = end;
      }
      const size = utf8Size(char.codePointAt(0)!);
      if (end - start === maxChars || bytes + size > CHUNK_BYTES) {
        break;
      }
      bytes += size;
    }
    // The rest fits, or the limit falls on a boundary, or the last boundary inside, or a word that fits nowhere
    const cut = end === chars.length || isSpace(chars[end]!) || boundary === -1 ? end : boundary;
    chunks.push(chars.slice(start, cut).join('').trimEnd());
    start = cut;
  }
  return chunks;
}

/**
 * Sends chunks one at a time, `gapMs` apart, through `send`. A switch or a
 * reconnect while chunks are waiting starts a new session, and the rest are
 * dropped rather than sent into the next game.
 */
export function sendChunked(chunks: readonly string[], send: (chunk: string) => void, gapMs: number): void {
  const started = session;
  let next = 0;
  const sendNext = (): void => {
    const chunk = chunks[next];
    if (chunk === undefined || session !== started) {
      return;
    }
    send(chunk);
    next++;
    if (next < chunks.length) {
      setTimeout(sendNext, gapMs);
    }
  };
  sendNext();
}

/** Chunks and sends public chat, team chat or a whisper. */
function sendText(text: string, send: (chunk: string) => void): void {
  sendChunked(chunkText(text, CHUNK_CHARS), send, CHUNK_GAP_MS);
}

/** Splits text into the chunks a chat, team or say send takes on the wire, for the senders outside the chat box. */
export function chunkForWire(text: string, say = false): string[] {
  return chunkText(text, say ? SAY_CHUNK_CHARS : CHUNK_CHARS);
}

/** Sends a single-shot SAY (emote or flag bubble) and arms the delivery
 *  watchdog, so a silently dropped bubble says so instead of looking broken.
 *  Only armed while PLAYING, because `sendSay` itself sends nothing otherwise.
 *  Typed `/s` text is deliberately not covered: it chunks into several sends,
 *  and it is typed with the chat feed already in front of you. */
function sendSayWatched(text: string): void {
  networkSendSay(text);

  if (session.state === GAME_STATE_CODES.PLAYING) {
    uiWatchSayEcho();
  }
}

/**
 * Resolve generic team-emote names to their team-specific variant.
 * `cap` → `bluecap` (blue) or `redcap` (red)
 * `burn` → `redfire` (blue) or `bluefire` (red)
 * `take` → `tred` (blue) or `tblue` (red)
 * Matches StarMash behavior (blue = team 1).
 */
function resolveTeamEmote(name: string): string {
  const blue = session.myTeam === 1;
  if (name === 'cap') return blue ? 'bluecap' : 'redcap';
  if (name === 'burn') return blue ? 'redfire' : 'bluefire';
  if (name === 'take') return blue ? 'tred' : 'tblue';
  return name;
}

/**
 * The player a command names, written escaped (as Tab completion and the
 * context menu write it) or as typed; says "Unknown player" otherwise.
 */
function playerNamed(name: string): Player | null {
  const player = playersGetByName(unescapePlayerName(name)) ?? playersGetByName(name);
  if (player === null) {
    addChatMessage('Unknown player');
  }
  return player;
}

/** The player a command takes as its whole argument, or the usage line when it has none. */
function playerArgument(rest: string, usage: string): Player | null {
  if (rest === '') {
    addChatMessage(usage, false);
    return null;
  }
  return playerNamed(rest);
}

function commandSay(rest: string): void {
  if (rest === '') {
    addChatMessage('Usage: /s message', false);
    return;
  }
  sendChunked(chunkText(rest, SAY_CHUNK_CHARS), networkSendSay, SAY_CHUNK_GAP_MS);
}

function commandTeam(rest: string): void {
  if (rest === '') {
    addChatMessage('Usage: /t message', false);
    return;
  }
  sendText(rest, networkSendTeam);
}

/** /w name message: the name is one word, escaped when it has spaces. */
function commandWhisper(rest: string): void {
  const space = rest.indexOf(' ');
  if (space === -1) {
    addChatMessage('Usage: /w player message', false);
    return;
  }
  const player = playerNamed(rest.slice(0, space));
  if (player !== null) {
    sendText(rest.slice(space + 1), (chunk) => networkSendWhisper(player.id, chunk));
  }
}

function commandIgnore(rest: string): void {
  const player = playerArgument(rest, 'Usage: /ignore player');
  if (player !== null) {
    chatIgnore(player.id);
  }
}

function commandUnignore(rest: string): void {
  const player = playerArgument(rest, 'Usage: /unignore player');
  if (player !== null) {
    chatUnignore(player);
  }
}

function commandVotemute(rest: string): void {
  const player = playerArgument(rest, 'Usage: /votemute player');
  if (player !== null) {
    chatVotemute(player);
  }
}

/** /spectate name watches that player; alone, it enters spectator mode (the server's -3). */
function commandSpectate(rest: string): void {
  if (rest === '') {
    networkSendCommand('spectate', '-3');
    return;
  }
  const player = playerNamed(rest);
  if (player !== null) {
    networkSendCommand('spectate', String(player.id));
  }
}

/** /flag name sets the flag; alone, or as /flags, it opens the menu's flag picker. */
function commandFlag(rest: string): void {
  if (rest === '') {
    openMainMenu('flag');
    return;
  }
  const flag = resolveFlag(rest);
  if (flag === null) {
    addChatMessage(`Unknown flag "${rest}". Try a country name, ISO code, or special flag (rainbow, scotland).`, false);
    return;
  }
  // The choice is kept and shown; the server gets the closest flag it has, and the chat says when that differs
  playersRequestMyFlag(flag);
  noteFlagGate(flag);
  networkSendCommand('flag', toServerFlag(flag));
}

/** /drop drops the flag in CTF, as the server's command; elsewhere it is short for /upgrades drop [n]. */
function commandDrop(rest: string): void {
  if (session.gameType === GAME_TYPES.CTF) {
    networkSendCommand('drop', rest);
  } else {
    networkSendCommand('upgrades', `drop ${firstWord(rest) || '1'}`);
  }
}

function commandUpgrade(rest: string): void {
  if (rest === '') {
    addChatMessage('Usage: /upgrade <1-4 or name> — speed, defense, energy, missile', false);
    return;
  }
  const name = firstWord(rest).toLowerCase();
  networkSendCommand('upgrade', UPGRADE_NAMES.get(name) ?? name);
}

function commandRespawn(rest: string): void {
  if (rest === '') {
    addChatMessage('Usage: /respawn <1-5 or name> — predator, goliath, mohawk, tornado, prowler', false);
    return;
  }
  const name = firstWord(rest).toLowerCase();
  networkSendCommand('respawn', PLANE_NAMES.get(name) ?? name);
}

function commandDebug(): void {
  // Vanilla's debug toggle: nothing here, and not a command for the server either
}

/**
 * The slash commands handled here, by name. Any other slash word is a team
 * emote, a flag bubble, an emote, or else a command for the server.
 */
const COMMANDS: ReadonlyMap<string, (rest: string) => void> = new Map<string, (rest: string) => void>([
  ['s', commandSay],
  ['t', commandTeam],
  ['w', commandWhisper],
  ['ignore', commandIgnore],
  ['unignore', commandUnignore],
  ['votemute', commandVotemute],
  ['spectate', commandSpectate],
  ['flag', commandFlag],
  ['flags', commandFlag],
  ['drop', commandDrop],
  ['upgrade', commandUpgrade],
  ['respawn', commandRespawn],
  ['emotes', uiToggleEmotes],
  ['amping', sendPing],
  ['help', toggleHelp],
  ['debug', commandDebug],
]);

/**
 * A slash word with no handler of its own: a flag bubble (/flagus), a team
 * emote (/cap, /burn, /take), an emote (/lol), or else a command for the
 * server with the rest of the line as its argument.
 */
function sendBubbleOrServerCommand(command: string, rest: string): void {
  // `/flag{code}` (e.g. /flagus, /flaggb-sct) → send as SAY bubble flag.
  // Dash-wrapped: live StarMash only renders -flagXX- bubbles (its colon
  // matching covers emotes only) and vanilla has no flag bubbles at all,
  // so dashes are the maximal-compat wire form. Emotes below stay
  // colon-wrapped because vanilla renders ONLY colons; StarMash takes both.
  if (command.startsWith('flag') && command.length > 4 && resolveFlag(command.slice(4)) !== null) {
    sendSayWatched(`-${command}-`);
    return;
  }
  const teamResolved = resolveTeamEmote(command);
  if (teamResolved !== command && isEmote(teamResolved, false)) {
    sendSayWatched(`:${teamResolved}:`);
  } else if (isEmote(command, false)) {
    sendSayWatched(`:${command}:`);
  } else {
    networkSendCommand(command, rest);
  }
}

/** A # line in CTF goes to the team as a QBot command, an alias expanded to the command the bots read. */
function sendTeamCommand(line: string): void {
  const space = line.indexOf(' ');
  const word = (space === -1 ? line : line.slice(0, space)).toLowerCase();
  const command = QBOT_ALIASES.get(word) ?? word;
  networkSendTeam(space === -1 ? command : command + line.slice(space));
}

/**
 * Routes a typed line: a slash command, a QBot team command in CTF, or plain
 * chat, chunked and spaced for the wire. A lone "/" is chat, as in vanilla.
 */
export function submitChatMessage(message: string): void {
  const trimmed = message.trim();
  if (trimmed.length === 0) {
    return;
  }

  // Mark hint as removed on first send attempt
  uiState.chat.isHintRemoved = true;

  if (trimmed.startsWith('/')) {
    const word = firstWord(trimmed);
    if (word.length > 1) {
      const command = word.slice(1).toLowerCase();
      const rest = trimmed.slice(word.length).trim();
      const handler = COMMANDS.get(command);
      if (handler !== undefined) {
        handler(rest);
      } else {
        sendBubbleOrServerCommand(command, rest);
      }
      return;
    }
  } else if (trimmed.startsWith('#') && session.gameType === GAME_TYPES.CTF) {
    sendTeamCommand(trimmed);
    return;
  }

  sendText(trimmed, networkSendChat);
}

/** The player behind a context menu, unless it is us. */
function otherPlayer(playerId: number): Player | null {
  const player = playersGet(playerId);
  if (player == null || playerId === session.myID) {
    return null;
  }
  return player;
}

/** Show context menu for a player at the specified screen position */
export function showContextMenu(playerId: number, top: number, left: number): void {
  const player = otherPlayer(playerId);
  if (player === null) {
    return;
  }

  uiState.contextMenu = {
    playerId,
    playerName: player.name,
    isIgnored: isPlayerIgnored(playerId),
    top,
    left,
  };
}

/** Open chat with a whisper command prefilled for the given player */
export function whisperPlayer(playerId: number): void {
  const player = otherPlayer(playerId);
  if (player === null) {
    return;
  }

  shortcutChat(`/w ${escapePlayerName(player.name)} `);
  uiState.contextMenu = null;
}

/** Toggle the ignore status of a player */
export function toggleIgnorePlayer(playerId: number): void {
  const player = otherPlayer(playerId);
  if (player === null) {
    return;
  }

  if (isPlayerIgnored(playerId)) {
    chatUnignore(player);
  } else {
    chatIgnore(playerId);
  }

  uiState.contextMenu = null;
}

/** Vote to mute a player */
export function votemutePlayer(playerId: number): void {
  const player = otherPlayer(playerId);
  if (player === null) {
    return;
  }

  chatVotemute(player);
  uiState.contextMenu = null;
}
